import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useState } from 'react'
import { TemplateEditor } from '@/components/templates/TemplateEditor'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  accountsQuery,
  reportsOverviewQuery,
  sharedTemplateQuery,
  useSetTemplateOwner,
} from '@/lib/queries'

/**
 * Editing a shared template. The same editor the account page opens — it takes the scope as a
 * prop, so the only differences are which routes it saves through and that the preview uses
 * Clipbook's brand, there being no account to borrow one from.
 *
 * This page is also where a freshly created template gets its home: the Account select assigns
 * it to an account (one-way — it then lives on that account's page, and this URL 404s). That is
 * a big enough change to confirm, and the confirmation says who is using the template: a report
 * in another account would silently fall back to the standard layout, which is why the server
 * refuses that case and the dialog explains it instead of letting the click through.
 */
export const Route = createFileRoute('/app/templates/$templateId')({
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(sharedTemplateQuery(params.templateId)),
      context.queryClient.ensureQueryData(accountsQuery),
      context.queryClient.ensureQueryData(reportsOverviewQuery),
    ]),
  component: SharedTemplateEditorPage,
})

function SharedTemplateEditorPage() {
  const { templateId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: template } = useSuspenseQuery(sharedTemplateQuery(templateId))
  const { data: accountsData } = useSuspenseQuery(accountsQuery)
  const { data: overview } = useSuspenseQuery(reportsOverviewQuery)
  // Our generalized owner-move (account or back to the library) — same wire call staging's
  // useMoveSharedTemplateToAccount made, so the confirm dialog below is unchanged.
  const move = useSetTemplateOwner(queryClient)
  const [pendingAccountId, setPendingAccountId] = useState<string | null>(null)

  const pendingAccount = accountsData.accounts.find((account) => account.id === pendingAccountId)
  const usedBy = overview.reports.filter((report) => report.templateId === templateId)
  const inAccount = usedBy.filter((report) => report.accountId === pendingAccountId).length
  const elsewhere = usedBy.length - inAccount
  // The standard layout is what every report WITHOUT a template renders through (CLI-838), so its
  // blast radius is the untemplated reports, not the ones naming its id — and it stays in the
  // library, because that is where the renderer looks for it.
  const isStandard = template.role === 'standard'
  const untemplated = overview.reports.filter((report) => report.templateId === null).length

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/app/templates"
          className="flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-3.5" />
          Template Library
        </Link>
        {isStandard ? (
          <span className="text-sm text-muted-foreground">
            Shared library — the standard layout
          </span>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Account</span>
            <SearchableSelect
              ariaLabel="Template account"
              value=""
              disabled={move.isPending}
              onValueChange={(accountId) => {
                if (accountId) setPendingAccountId(accountId)
              }}
              options={[
                { value: '', label: move.isPending ? 'Moving…' : 'Shared library' },
                ...accountsData.accounts.map((account) => ({
                  value: account.id,
                  label: account.name,
                  group: 'Accounts',
                })),
              ]}
              searchPlaceholder="Account name…"
              className="w-56"
            />
          </div>
        )}
      </div>
      {isStandard && (
        <p
          role="note"
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm"
        >
          This is the standard layout: every report without a template renders through it —{' '}
          {untemplated} {untemplated === 1 ? 'report' : 'reports'} today. Saving changes all of
          them. It can be edited and renamed, never deleted or moved to an account.
        </p>
      )}
      {move.isError && (
        <p role="alert" className="text-sm text-destructive">
          {move.error.message}
        </p>
      )}
      {/* Keyed by id so navigating between templates never carries one draft into another. */}
      <TemplateEditor key={template.id} scope={{ kind: 'shared' }} template={template} />

      {pendingAccountId && pendingAccount && (
        <ConfirmDialog
          open
          onClose={() => setPendingAccountId(null)}
          title={`Move “${template.name}” to ${pendingAccount.name}?`}
          description={`One-way: the template leaves the shared library, and from then on only ${pendingAccount.name}’s reports can pick it. To keep a shared copy as well, duplicate it first.`}
          body={
            <ul className="flex flex-col gap-1 text-sm">
              <li>
                {usedBy.length === 0
                  ? 'No report uses it yet.'
                  : `Used by ${inAccount} ${inAccount === 1 ? 'report' : 'reports'} in ${pendingAccount.name}.`}
              </li>
              {elsewhere > 0 && (
                <li className="text-destructive">
                  Used by {elsewhere} {elsewhere === 1 ? 'report' : 'reports'} in other accounts —
                  their emails would fall back to the standard layout, so this move is refused.
                  Duplicate the template into {pendingAccount.name} instead.
                </li>
              )}
            </ul>
          }
          confirmLabel={`Move to ${pendingAccount.name}`}
          pendingLabel="Moving…"
          pending={move.isPending}
          disabled={elsewhere > 0}
          onConfirm={() =>
            move.mutate(
              { templateId, accountId: pendingAccountId },
              {
                onSuccess: () => {
                  void navigate({
                    to: '/app/accounts/$accountId/templates/$templateId',
                    params: { accountId: pendingAccountId, templateId },
                  }).then(() =>
                    // Only once this page is gone: while it is mounted, removing the query it
                    // observes just makes it refetch — and the template is not shared any more.
                    queryClient.removeQueries({
                      queryKey: sharedTemplateQuery(templateId).queryKey,
                      exact: true,
                    }),
                  )
                },
                onSettled: () => setPendingAccountId(null),
              },
            )
          }
        />
      )}
    </div>
  )
}
